import React from "react";
import { Alert, Box, Breadcrumbs, CircularProgress, Pagination, Typography } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import JumboListNoDataPlaceholder from "@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder";
import NoDataPlaceholder from "app/shared/NoDataPlaceholder";
import { DsokManagementListToolbar } from "app/pages/customers/dsok/components/DsokManagementListToolbar";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { getDSOKProducts } from "app/services/api/management";
import { getAccessSystemVouchers } from "app/services/api/accessSystemSettings";
import {
  normalizeDsokProductsList,
  resolveProductDisplayName,
} from "./voucherTicketProducts";
import { VOUCHERS_MANAGEMENT_BASE_PATH } from "./vouchersConstants";
import VoucherSeriesListItem from "./components/VoucherSeriesListItem";
import { getLoggedInCustomerId } from "./accessSystemLoggedInCustomer";

const VOUCHERS_I18N = "pages.dsokDetails.accessSystem.vouchers";

const DEFAULT_PAGE_SIZE = 20;

/** Search persistence key — aligned with JumboSearch `page` prop pattern */
const VOUCHERS_SEARCH_PAGE = "AccessSystemVouchers";

/** @param {string} isoDate YYYY-MM-DD */
const formatValidUntilDisplay = (isoDate) => {
  if (!isoDate || typeof isoDate !== "string") return "—";
  const d = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(d.getTime())) return isoDate;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

/**
 * Client-side filter on the current API page rows only.
 * @param {(row: object) => string} getProductLabel — resolved ticket/product display text (not raw id).
 */
function filterVoucherRows(rows, queryRaw, getProductLabel) {
  const q = String(queryRaw ?? "")
    .trim()
    .toLowerCase();
  if (!q) return rows;
  return (rows || []).filter((row) => {
    const name = String(row.name ?? "").toLowerCase();
    const productLabel = String(getProductLabel?.(row) ?? "").toLowerCase();
    const status = String(row.status ?? "").toLowerCase();
    const validUntil = String(row.validUntil ?? "").toLowerCase();
    const ticket = String(row.ticketProduct ?? "").toLowerCase();
    const productIdsStr = Array.isArray(row.productIds)
      ? row.productIds.map((x) => String(x).toLowerCase()).join(" ")
      : "";
    const codes = Array.isArray(row.vouchers)
      ? row.vouchers.map((v) => String(v.code ?? "").toLowerCase()).join(" ")
      : "";
    return (
      name.includes(q) ||
      productLabel.includes(q) ||
      status.includes(q) ||
      validUntil.includes(q) ||
      ticket.includes(q) ||
      productIdsStr.includes(q) ||
      codes.includes(q)
    );
  });
}

/**
 * Access System — voucher series list (GET /api/v1/access-system/vouchers with pagination).
 */
const AccessSystemVouchersPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { getAuthUser } = useJumboAuth();
  const customer = getAuthUser()?.customer;
  const loggedInCustomerId = getLoggedInCustomerId(getAuthUser);
  const defaultLanguage =
    customer?.dsokSettings?.customer?.defaultLanguage || i18n.language || "sk";

  const [rows, setRows] = React.useState([]);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [apiPage, setApiPage] = React.useState(1);
  const [pagination, setPagination] = React.useState({
    page: 1,
    limit: DEFAULT_PAGE_SIZE,
    total: 0,
    pages: 1,
  });
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(false);
  const [dsokProducts, setDsokProducts] = React.useState([]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = await getDSOKProducts();
        const list = normalizeDsokProductsList(raw);
        if (!cancelled) setDsokProducts(list);
      } catch (_e) {
        if (!cancelled) setDsokProducts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const res = await getAccessSystemVouchers({
          page: apiPage,
          limit: DEFAULT_PAGE_SIZE,
          ...(loggedInCustomerId ? { customerId: loggedInCustomerId } : {}),
        });
        if (cancelled) return;
        if (res && res.success === true && Array.isArray(res.data)) {
          setRows(res.data);
          const p = res.pagination;
          setPagination({
            page: p && typeof p.page === "number" ? p.page : apiPage,
            limit: p && typeof p.limit === "number" ? p.limit : DEFAULT_PAGE_SIZE,
            total: p && typeof p.total === "number" ? p.total : res.data.length,
            pages: p && typeof p.pages === "number" ? p.pages : 1,
          });
          setLoadError(false);
        } else {
          setRows([]);
          setLoadError(true);
        }
      } catch (_err) {
        if (!cancelled) {
          setRows([]);
          setLoadError(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiPage, loggedInCustomerId]);

  const productById = React.useMemo(() => {
    const m = new Map();
    (dsokProducts || []).forEach((p) => {
      const id = p?._id != null ? String(p._id) : "";
      if (id) m.set(id, p);
    });
    return m;
  }, [dsokProducts]);

  const resolveTicketTypeLabel = React.useCallback(
    (item) => {
      if (!item) return "—";
      const ids = Array.isArray(item.productIds) ? item.productIds : [];
      if (ids.length > 0) {
        const labels = ids.map((id) => {
          const p = productById.get(String(id));
          if (p) {
            const name = resolveProductDisplayName(p, defaultLanguage);
            if (name && String(name).trim()) return String(name).trim();
          }
          return t(`${VOUCHERS_I18N}.series.unknownProduct`, {
            defaultValue: "Neznámy produkt",
          });
        });
        return labels.join(", ");
      }
      const pid = item.productId;
      if (pid != null && pid !== "") {
        const p = productById.get(String(pid));
        if (p) {
          const name = resolveProductDisplayName(p, defaultLanguage);
          if (name && String(name).trim()) return String(name).trim();
        }
        return t(`${VOUCHERS_I18N}.series.unknownProduct`, {
          defaultValue: "Neznámy produkt",
        });
      }
      if (item.ticketProduct != null && String(item.ticketProduct).trim() !== "") {
        return String(item.ticketProduct).trim();
      }
      return "—";
    },
    [productById, defaultLanguage, t],
  );

  const filteredRows = React.useMemo(
    () => filterVoucherRows(rows, searchQuery, resolveTicketTypeLabel),
    [rows, searchQuery, resolveTicketTypeLabel],
  );

  const title = t("sidebar.menuItem.accessSystemVouchers", {
    defaultValue: "Správa poukážok",
  });

  const resolveStatusLabel = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "active") {
      return t(`${VOUCHERS_I18N}.status.active`, { defaultValue: "Active" });
    }
    if (s === "inactive") {
      return t(`${VOUCHERS_I18N}.status.inactive`, { defaultValue: "Inactive" });
    }
    return status || "—";
  };

  const searchPlaceholder = t(`${VOUCHERS_I18N}.list.searchPlaceholder`, {
    defaultValue: "Vyhľadať sériu, typ lístka alebo kód poukážky",
  });

  const addTooltip = t(`${VOUCHERS_I18N}.list.addNewRecord`, {
    defaultValue: "Pridať nový záznam",
  });

  const loadErrorMessage = t(`${VOUCHERS_I18N}.list.loadError`, {
    defaultValue: "Nepodarilo sa načítať poukážky.",
  });

  const pageCount = Math.max(1, pagination.pages || 1);

  const renderMainContent = () => {
    if (loading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={200}
          sx={{
            width: "100%",
            backgroundColor: "background.paper",
            borderRadius: 1,
            boxShadow: 1,
            py: 4,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (loadError) {
      return (
        <Box
          sx={{
            width: "100%",
            backgroundColor: "background.paper",
            borderRadius: 1,
            boxShadow: 1,
            p: 2,
          }}
        >
          <Alert severity="error">{loadErrorMessage}</Alert>
        </Box>
      );
    }

    if (!rows.length) {
      return (
        <JumboListNoDataPlaceholder>
          <NoDataPlaceholder />
        </JumboListNoDataPlaceholder>
      );
    }

    if (!filteredRows.length) {
      return (
        <JumboListNoDataPlaceholder>
          <NoDataPlaceholder />
        </JumboListNoDataPlaceholder>
      );
    }

    return (
      <>
        {filteredRows.map((row) => (
          <VoucherSeriesListItem
            key={row.id}
            item={row}
            ticketTypeLabel={resolveTicketTypeLabel(row)}
            onOpen={(id) => navigate(`${VOUCHERS_MANAGEMENT_BASE_PATH}/${id}`)}
            formatValidUntilDisplay={formatValidUntilDisplay}
            resolveStatusLabel={resolveStatusLabel}
          />
        ))}
      </>
    );
  };

  return (
    <React.Fragment>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <Link to="/">{t("sidebar.menuItem.home")}</Link>
        <Link to="/customer/dsok/access-system/tickets-management">
          {t("sidebar.menuItem.accessSystem")}
        </Link>
        <Typography color="text.primary">{title}</Typography>
      </Breadcrumbs>

      <Typography variant="h2" mb={3}>
        {title}
      </Typography>

      <DsokManagementListToolbar
        searchPage={VOUCHERS_SEARCH_PAGE}
        onSearchChange={setSearchQuery}
        searchPlaceholder={searchPlaceholder}
        addTooltip={addTooltip}
        onAddClick={() => navigate(`${VOUCHERS_MANAGEMENT_BASE_PATH}/new`)}
        rightSlot={
          <Pagination
            count={pageCount}
            color="primary"
            page={apiPage}
            onChange={(_, val) => setApiPage(val)}
            disabled={loading}
          />
        }
      />

      {renderMainContent()}
    </React.Fragment>
  );
};

export default AccessSystemVouchersPage;
